import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import updateAiContentProjectVariationInputSchema from 'common/src/modules/aiContent/store/aiContentProject/thunks/updateAiContentProjectVariationInputSchema';
import {
  AiAiContentVariationColumnDataType,
  AiContentVariationColumnDataType,
} from 'common/src/modules/aiContent/aiContentProject.types';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {AiContentProjectAgentToolName} from '../aiContentProjectAgent.enums';

const inputSchema = z.object({
  columns: z
    .array(
      z.object({
        name: z.string().describe('Column name/identifier'),
        dataType: z
          .nativeEnum(AiAiContentVariationColumnDataType)
          .describe('Data type of the column'),
        isRequired: z.boolean().describe('Whether this column is required'),
        usedFor: z
          .string()
          .optional()
          .nullable()
          .describe('Description of what this column is used for'),
      }),
    )
    .describe(
      'The complete list of input parameter columns. This replaces all existing columns.',
    ),
});

export default function updateInputParametersTool(store: CLStore) {
  async function execute({
    input: {columns},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const project = state.aiContentProjectPage?.project;

    if (!project?.id) {
      return toolResult('Error: No project is currently loaded. Cannot update input parameters.');
    }

    if (!columns || columns.length === 0) {
      return toolResult(
        'Error: No columns provided. Please specify at least one input parameter column.',
      );
    }

    // Convert string enum values to numeric enum values
    const convertedColumns = columns.map((column) => {
      const numericDataType = getMatchingEnumValue(
        AiAiContentVariationColumnDataType,
        AiContentVariationColumnDataType,
        column.dataType,
      );

      if (numericDataType === undefined) {
        throw new Error(`Invalid dataType: ${column.dataType}`);
      }

      return {
        name: column.name,
        dataType: numericDataType,
        isRequired: column.isRequired,
        usedFor: column.usedFor ?? null,
        source: null,
      };
    });

    try {
      await store
        .dispatch(
          updateAiContentProjectVariationInputSchema({
            projectId: project.id,
            variationInputSchema: {columns: convertedColumns},
          }),
        )
        .unwrap();

      const columnNames = columns.map((col) => col.name).join(', ');

      return toolResult(
        `Successfully updated input parameters. Columns: ${columnNames} (${columns.length} total).`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolResult(`Error updating input parameters: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Updating input parameters',
    name: AiContentProjectAgentToolName.UpdateInputParameters,
    description:
      'Updates the input parameters (variation columns) for the AI Content Project. Provide the complete list of columns - this will replace all existing input parameters.',
    execute,
    inputSchema,
  });
}

